import { useCallback, useEffect, useState } from "react";
import { useViewportSize } from "@mantine/hooks";

export const PANEL_LIMITS = {
  left: { min: 180, max: 480, initial: 280 },
  right: { min: 200, max: 520, initial: 300 },
} as const;

/** Narrowest the canvas may get before a panel stops growing. */
export const CANVAS_MIN_WIDTH = 480;
/** Width an open panel's edge or a hidden panel's rail takes, mirrored in PanelEdge.module.scss. */
const EDGE_WIDTH = 12;
const RAIL_WIDTH = 32;

const STORAGE_KEY = "form-builder-editor-panels";

type Side = "left" | "right";

interface EditorPanelsState {
  leftWidth: number;
  rightWidth: number;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
}

const INITIAL_STATE: EditorPanelsState = {
  leftWidth: PANEL_LIMITS.left.initial,
  rightWidth: PANEL_LIMITS.right.initial,
  leftCollapsed: false,
  rightCollapsed: false,
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function readStoredState(): EditorPanelsState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_STATE;
    const stored = JSON.parse(raw) as Partial<EditorPanelsState>;
    return {
      leftWidth: clamp(
        Number(stored.leftWidth) || PANEL_LIMITS.left.initial,
        PANEL_LIMITS.left.min,
        PANEL_LIMITS.left.max,
      ),
      rightWidth: clamp(
        Number(stored.rightWidth) || PANEL_LIMITS.right.initial,
        PANEL_LIMITS.right.min,
        PANEL_LIMITS.right.max,
      ),
      leftCollapsed: stored.leftCollapsed === true,
      rightCollapsed: stored.rightCollapsed === true,
    };
  } catch {
    return INITIAL_STATE;
  }
}

/**
 * The widest a panel may be right now: its own limit, but never so wide that the canvas drops
 * below CANVAS_MIN_WIDTH next to the other panel as it currently stands.
 */
function panelMax(side: Side, viewport: number, otherOccupied: number, collapsedSelf: boolean) {
  const limits = PANEL_LIMITS[side];
  const ownEdge = collapsedSelf ? RAIL_WIDTH : EDGE_WIDTH;
  const room = viewport - CANVAS_MIN_WIDTH - otherOccupied - ownEdge;
  return clamp(room, limits.min, limits.max);
}

/** Widths as they render: the stored ones, pulled in when the viewport no longer fits them. */
function resolveWidths(state: EditorPanelsState, viewport: number) {
  const rightOccupiedAtStored = state.rightCollapsed ? RAIL_WIDTH : state.rightWidth + EDGE_WIDTH;
  const leftMax = panelMax("left", viewport, rightOccupiedAtStored, state.leftCollapsed);
  const leftWidth = Math.min(state.leftWidth, leftMax);
  const leftOccupied = state.leftCollapsed ? RAIL_WIDTH : leftWidth + EDGE_WIDTH;
  const rightMax = panelMax("right", viewport, leftOccupied, state.rightCollapsed);
  const rightWidth = Math.min(state.rightWidth, rightMax);
  return { leftWidth, leftMax, rightWidth, rightMax };
}

export interface EditorPanel {
  width: number;
  max: number;
  collapsed: boolean;
  resize: (delta: number) => void;
  resizeTo: (width: number) => void;
  toggle: () => void;
}

export function useEditorPanels(): Record<Side, EditorPanel> {
  const [state, setState] = useState<EditorPanelsState>(readStoredState);
  // useViewportSize reports 0 until its first effect; the window is already measurable.
  const { width: measured } = useViewportSize();
  const viewport = measured || window.innerWidth;

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage can be unavailable (private mode, quota); the layout still works for this visit.
    }
  }, [state]);

  const { leftWidth, leftMax, rightWidth, rightMax } = resolveWidths(state, viewport);

  // A drag fires many moves from a handler bound at pointerdown, so each step is applied to the
  // state as it is when it runs, never to the width the handler saw when the drag began.
  const setWidth = useCallback(
    (side: Side, width: number | ((current: number) => number)) =>
      setState((s) => {
        const resolved = resolveWidths(s, window.innerWidth);
        const current = side === "left" ? resolved.leftWidth : resolved.rightWidth;
        const max = side === "left" ? resolved.leftMax : resolved.rightMax;
        const requested = typeof width === "function" ? width(current) : width;
        const next = clamp(requested, PANEL_LIMITS[side].min, max);
        return side === "left" ? { ...s, leftWidth: next } : { ...s, rightWidth: next };
      }),
    [],
  );

  const toggle = useCallback(
    (side: Side) =>
      setState((s) =>
        side === "left"
          ? { ...s, leftCollapsed: !s.leftCollapsed }
          : { ...s, rightCollapsed: !s.rightCollapsed },
      ),
    [],
  );

  const build = (side: Side, width: number, max: number, collapsed: boolean): EditorPanel => ({
    width,
    max,
    collapsed,
    resize: (delta) => setWidth(side, (current) => current + delta),
    resizeTo: (next) => setWidth(side, next),
    toggle: () => toggle(side),
  });

  return {
    left: build("left", leftWidth, leftMax, state.leftCollapsed),
    right: build("right", rightWidth, rightMax, state.rightCollapsed),
  };
}
